import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  HeartHandshake,
  ShieldAlert,
  Users,
  Phone,
  MapPin,
  Search,
  Plus,
  UserPlus,
  UserMinus,
  FileText,
  AlertTriangle,
  DollarSign,
  Trash2,
  Edit3,
  Loader2,
  Save,
  MessageSquare,
  User,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ClipboardList,
  Download,
  ExternalLink,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissao } from '../../hooks/usePermissao';
import { useToast, useConfirm } from '../../Components/Feedback';
import { useApi } from '../../api/useApi';
import { Modal, Input, Select, Button, Card } from '../../Components/ui';

function getTodayLocal() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const date = new Date(d.getTime() - offset * 60 * 1000);
  return date.toISOString().split('T')[0];
}

const CORES_VULNERABILIDADE = {
  critica: {
    bg: 'bg-red-50 dark:bg-red-950/40',
    border: 'border-red-200 dark:border-red-800',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300 border-red-200 dark:border-red-800',
    label: 'Crítica',
  },
  alta: {
    bg: 'bg-orange-50 dark:bg-orange-950/40',
    border: 'border-orange-200 dark:border-orange-800',
    badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300 border-orange-200 dark:border-orange-800',
    label: 'Alta',
  },
  media: {
    bg: 'bg-amber-50 dark:bg-amber-950/40',
    border: 'border-amber-200 dark:border-amber-800',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    label: 'Média',
  },
  baixa: {
    bg: 'bg-emerald-50 dark:bg-emerald-950/40',
    border: 'border-emerald-200 dark:border-emerald-800',
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
    label: 'Baixa',
  },
  nao_informado: {
    bg: 'bg-gray-50 dark:bg-gray-900/40',
    border: 'border-gray-200 dark:border-gray-700',
    badge: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400 border-gray-200 dark:border-gray-700',
    label: 'Não Informado',
  },
};

const ROTULOS_MORADIA = {
  propria: 'Própria',
  alugada: 'Alugada',
  cedida: 'Cedida / Emprestada',
  ocupacao: 'Ocupação / Assentamento',
  financiada: 'Financiada',
  outro: 'Outro',
};

const ROTULOS_TRABALHO = {
  formal_clt: 'Formal (CLT)',
  autonomo: 'Autônomo / Informal',
  desempregado: 'Desempregado(a)',
  aposentado: 'Aposentado(a) / Pensionista',
  outro: 'Outro',
};

const ROTULOS_BENEFICIO = {
  nenhum: 'Nenhum',
  bolsa_familia: 'Bolsa Família',
  bpc: 'BPC',
  tarifa_social: 'Tarifa Social',
  outro: 'Outro',
};

const TIPOS_ATENDIMENTO_LEGADO = [
  { value: 'presencial', label: 'Atendimento Presencial' },
  { value: 'visita_domiciliar', label: 'Visita Domiciliar' },
  { value: 'telefone', label: 'Contato Telefônico' },
  { value: 'reuniao_familiar', label: 'Reunião Familiar' },
  { value: 'outro', label: 'Outro' },
];

// Catálogo padrão de Atividades baseado na planilha real do Serviço Social
const ATIVIDADES_PADRAO = [
  'Atendimentos - Inscritos',
  'Atendimentos - Inscritos laudados',
  'Atendimentos - Familiares',
  'Atendimentos - Não inscritos',
  'Atendimentos - Inadimplentes',
  'Atendimentos - Equipe de trabalho',
  'Visitas Sociais',
  'Reunião Interna - Equipe de Trabalho',
  'Reunião Interna - Familiares',
  'Reunião Externa - Instituições Públicas',
  'Reunião Externa - Instituições Privadas',
  'Encaminhamentos Sociais',
  'Criação de Relatórios',
  'Produção de documentos',
  'Produção de ofícios',
  'Gestão de cestas básicas',
  'Gestão de doações',
  'Análise Sócio-Econômica',
  'Capacitação / Seminário - realização',
  'Capacitação / Seminário - participação',
];

const PUBLICOS_ALVO_PADRAO = [
  'Inscritos',
  'Familiares',
  'Equipe de trabalho',
  'Instituições públicas',
  'Instituições privadas',
  'Não inscritos',
  'Comunidade',
];

const ASSUNTOS_PADRAO = [
  'Vulnerabilidade - financeira',
  'Vulnerabilidade - emocional',
  'Vulnerabilidade - física',
  'Vulnerabilidade - ambas',
  'Parcerias',
  'Operação',
];

function formatarIdade(idade, dataNascimento) {
  if (idade !== null && idade !== undefined && !isNaN(idade)) {
    return `${idade} anos`;
  }
  if (dataNascimento) {
    const hoje = new Date();
    const nasc = new Date(dataNascimento);
    let anos = hoje.getFullYear() - nasc.getFullYear();
    const m = hoje.getMonth() - nasc.getMonth();
    if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) anos--;
    if (!isNaN(anos) && anos >= 0) return `${anos} anos`;
  }
  return 'Idade não informada';
}

function AvatarAluno({ fotoUrl, nome, tamanho = 'md', className = '' }) {
  const tamanhos = {
    xs: 'w-6 h-6',
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-14 h-14',
  };

  const tamanhoClasse = tamanhos[tamanho] || tamanhos.md;

  return (
    <div
      className={`${tamanhoClasse} rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center flex-shrink-0 relative ${className}`}
    >
      {fotoUrl ? (
        <img src={fotoUrl} alt={nome || 'Aluno'} className="w-full h-full object-cover" />
      ) : (
        <User className="w-1/2 h-1/2 text-gray-400 dark:text-gray-500" />
      )}
    </div>
  );
}

function AssistenciaSocial() {
  const { institutionId, institutionName } = useInstitution();
  const { user } = useAuth();
  const { podeCriar, podeEditar, podeExcluir, podeExportar } = usePermissao('/assistencia-social');
  const toast = useToast();
  const confirm = useConfirm();
  const api = useApi();
  const queryClient = useQueryClient();

  // Alternância de Abas Principais: 'diario' (Nova gestão de ações da planilha) vs 'prontuarios' (Painel atual de alunos)
  const [abaPrincipal, setAbaPrincipal] = useState('diario');

  // --------------------------------------------------------------------------
  // ESTADOS DO DIÁRIO DE AÇÕES SOCIAIS
  // --------------------------------------------------------------------------
  const [filtrosAcoes, setFiltrosAcoes] = useState({
    busca: '',
    data_inicio: '',
    data_fim: '',
    atividade: 'todos',
    publico_alvo: 'todos',
    assunto: 'todos',
    assistente: 'todos',
  });

  const [modalAcaoAberto, setModalAcaoAberto] = useState(false);
  const [acaoEmEdicao, setAcaoEmEdicao] = useState(null);
  const [formAcao, setFormAcao] = useState({
    data_acao: getTodayLocal(),
    nome_assistente_social: user?.nome || '',
    atividade: 'Atendimentos - Inscritos',
    publico_alvo: 'Inscritos',
    assunto: 'Vulnerabilidade - financeira',
    nome_familiar: '',
    id_aluno: '',
    nome_inscrito: '',
    nome_equipe: '',
    nome_externo: '',
    observacoes: '',
  });

  const [observacaoExpandidaId, setObservacaoExpandidaId] = useState(null);

  // --------------------------------------------------------------------------
  // ESTADOS DOS PRONTUÁRIOS (TELA EXISTENTE PRESERVADA)
  // --------------------------------------------------------------------------
  const [busca, setBusca] = useState('');
  const [filtroVulnerabilidade, setFiltroVulnerabilidade] = useState('todos');
  const [filtroBeneficio, setFiltroBeneficio] = useState('todos');
  const [filtroAlerta, setFiltroAlerta] = useState('todos');
  const [ordenacao, setOrdenacao] = useState({ campo: 'vulnerabilidade', direcao: 'asc' });

  const [alunoProntuarioId, setAlunoProntuarioId] = useState(null);
  const [abaProntuario, setAbaProntuario] = useState('diagnostico');
  const [modalEditarPerfilAberto, setModalEditarPerfilAberto] = useState(false);
  const [modalAtendimentoAberto, setModalAtendimentoAberto] = useState(false);
  const [novoAtendimento, setNovoAtendimento] = useState({
    tipo: 'presencial',
    data_atendimento: getTodayLocal(),
    descricao: '',
    encaminhamento: '',
    beneficio_concedido: '',
  });

  const [perfilEditavel, setPerfilEditavel] = useState({});
  const [modalIncluirAberto, setModalIncluirAberto] = useState(false);
  const [buscaCandidato, setBuscaCandidato] = useState('');
  const [candidatoSelecionado, setCandidatoSelecionado] = useState(null);
  const [dadosInclusao, setDadosInclusao] = useState({
    renda_familiar: '',
    moradores_casa: '',
    moradia_tipo: '',
    trabalho_responsaveis: '',
    recebe_beneficio: '',
    nis: '',
    vulnerabilidade: 'nao_informado',
    orgaos_acompanhamento: '',
    observacoes_social: '',
  });

  // --------------------------------------------------------------------------
  // CONSULTAS (REACT QUERY)
  // --------------------------------------------------------------------------

  // 1. Carregar Ações Sociais (Diário de Bordo)
  const { data: dadosAcoes, isLoading: carregandoAcoes } = useQuery({
    queryKey: ['assistencia-social-acoes', institutionId, filtrosAcoes],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filtrosAcoes.busca) params.append('busca', filtrosAcoes.busca);
      if (filtrosAcoes.data_inicio) params.append('data_inicio', filtrosAcoes.data_inicio);
      if (filtrosAcoes.data_fim) params.append('data_fim', filtrosAcoes.data_fim);
      if (filtrosAcoes.atividade !== 'todos') params.append('atividade', filtrosAcoes.atividade);
      if (filtrosAcoes.publico_alvo !== 'todos') params.append('publico_alvo', filtrosAcoes.publico_alvo);
      if (filtrosAcoes.assunto !== 'todos') params.append('assunto', filtrosAcoes.assunto);
      if (filtrosAcoes.assistente !== 'todos') params.append('assistente', filtrosAcoes.assistente);
      const res = await api.get(`/api/assistencia-social/acoes?${params.toString()}`);
      return res?.data ?? res;
    },
    enabled: !!institutionId,
  });

  // 1.1 Opções registradas para filtros
  const { data: opcoesFiltros } = useQuery({
    queryKey: ['assistencia-social-opcoes', institutionId],
    queryFn: async () => {
      const res = await api.get('/api/assistencia-social/acoes/opcoes');
      return res?.data ?? res;
    },
    enabled: !!institutionId,
  });

  // 1.2 Lista de todos os alunos ativos da instituição (para vincular na ação)
  const { data: listaAlunosInst = [] } = useQuery({
    queryKey: ['alunos-seletor-assistencia', institutionId],
    queryFn: async () => {
      const res = await api.get('/api/alunos?status=todos');
      const lista = res?.data ?? (Array.isArray(res) ? res : []);
      return Array.isArray(lista) ? lista : [];
    },
    enabled: !!institutionId,
  });

  // 2. Carregar lista geral e resumo de alunos (Prontuários)
  const { data: resposta, isLoading } = useQuery({
    queryKey: ['assistencia-social', institutionId],
    queryFn: async () => {
      const res = await api.get('/api/assistencia-social');
      return res?.data ?? res;
    },
    enabled: !!institutionId,
  });

  // 2.1 Carregar candidatos da instituição para inclusão
  const { data: candidatos = [], isLoading: carregandoCandidatos } = useQuery({
    queryKey: ['candidatos-sociais', institutionId, buscaCandidato],
    queryFn: async () => {
      const res = await api.get(
        `/api/assistencia-social/candidatos?busca=${encodeURIComponent(buscaCandidato)}`,
      );
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!modalIncluirAberto && !!institutionId,
  });

  const resumo = resposta?.resumo || {
    total_alunos: 0,
    total_com_acompanhamento: 0,
    total_critica: 0,
    total_alta: 0,
    total_bolsa_familia: 0,
    total_alerta_faltas: 0,
    atendimentos_mes: 0,
  };

  const alunos = useMemo(() => resposta?.alunos || [], [resposta?.alunos]);

  // 3. Carregar prontuário do aluno selecionado
  const {
    data: prontuario,
    isLoading: carregandoProntuario,
    refetch: recarregarProntuario,
  } = useQuery({
    queryKey: ['prontuario-social', alunoProntuarioId, institutionId],
    queryFn: async () => {
      const res = await api.get(`/api/assistencia-social/${alunoProntuarioId}/prontuario`);
      return res?.data ?? res;
    },
    enabled: !!alunoProntuarioId && !!institutionId,
  });

  // --------------------------------------------------------------------------
  // MUTAÇÕES DO DIÁRIO DE AÇÕES SOCIAIS
  // --------------------------------------------------------------------------

  const mutationSalvarAcao = useMutation({
    mutationFn: async (dados) => {
      if (acaoEmEdicao) {
        return api.put(`/api/assistencia-social/acoes/${acaoEmEdicao.id}`, dados);
      }
      return api.post('/api/assistencia-social/acoes', dados);
    },
    onSuccess: (res) => {
      toast.success(
        res?.message || (acaoEmEdicao ? 'Ação social atualizada.' : 'Ação social registrada com sucesso.'),
      );
      setModalAcaoAberto(false);
      setAcaoEmEdicao(null);
      queryClient.invalidateQueries({ queryKey: ['assistencia-social-acoes', institutionId] });
      queryClient.invalidateQueries({ queryKey: ['assistencia-social-opcoes', institutionId] });
      if (alunoProntuarioId) recarregarProntuario();
    },
    onError: (err) => {
      toast.error(err.message || 'Erro ao salvar ação social.');
    },
  });

  const mutationExcluirAcao = useMutation({
    mutationFn: async (idAcao) => {
      return api.del(`/api/assistencia-social/acoes/${idAcao}`);
    },
    onSuccess: (res) => {
      toast.success(res?.message || 'Ação social removida com sucesso.');
      queryClient.invalidateQueries({ queryKey: ['assistencia-social-acoes', institutionId] });
      if (alunoProntuarioId) recarregarProntuario();
    },
    onError: (err) => {
      toast.error(err.message || 'Erro ao remover ação social.');
    },
  });

  const abrirModalNovaAcao = (alunoPredefinido = null) => {
    setAcaoEmEdicao(null);
    setFormAcao({
      data_acao: getTodayLocal(),
      nome_assistente_social: user?.nome || '',
      atividade: 'Atendimentos - Inscritos',
      publico_alvo: 'Inscritos',
      assunto: 'Vulnerabilidade - financeira',
      nome_familiar: alunoPredefinido?.responsavel_nome || '',
      id_aluno: alunoPredefinido ? String(alunoPredefinido.id) : '',
      nome_inscrito: alunoPredefinido ? alunoPredefinido.nome : '',
      nome_equipe: '',
      nome_externo: '',
      observacoes: '',
    });
    setModalAcaoAberto(true);
  };

  const abrirModalEditarAcao = (acao) => {
    setAcaoEmEdicao(acao);
    setFormAcao({
      data_acao: acao.data_acao || getTodayLocal(),
      nome_assistente_social: acao.nome_assistente_social || user?.nome || '',
      atividade: acao.atividade || 'Atendimentos - Inscritos',
      publico_alvo: acao.publico_alvo || 'Inscritos',
      assunto: acao.assunto || 'Vulnerabilidade - financeira',
      nome_familiar: acao.nome_familiar || '',
      id_aluno: acao.id_aluno ? String(acao.id_aluno) : '',
      nome_inscrito: acao.nome_inscrito || '',
      nome_equipe: acao.nome_equipe || '',
      nome_externo: acao.nome_externo || '',
      observacoes: acao.observacoes || '',
    });
    setModalAcaoAberto(true);
  };

  const handleExcluirAcaoConfirmada = async (acao) => {
    const ok = await confirm(
      `Deseja realmente excluir a ação "${acao.atividade}" realizada em ${formatarDataBR(acao.data_acao)}?`,
      { title: 'Remover ação social', confirmText: 'Excluir', tone: 'danger' },
    );
    if (!ok) return;
    mutationExcluirAcao.mutate(acao.id);
  };

  // Exportação Excel fiel às 11 colunas da planilha do usuário
  const exportarExcelAcoes = () => {
    const acoes = dadosAcoes?.acoes || [];
    if (!acoes.length) {
      toast.warning('Nenhuma ação encontrada para exportação.');
      return;
    }

    const projetoSigla =
      institutionId === 1
        ? 'CSertão'
        : institutionId === 2
          ? 'CCLargo'
          : institutionName || `Inst_${institutionId}`;

    const linhasExportacao = acoes.map((a) => ({
      PROJETO: projetoSigla,
      DATA: a.data_acao ? formatarDataBR(a.data_acao) : '',
      'ASSISTENTE SOCIAL': a.nome_assistente_social || '',
      ATIVIDADE: a.atividade || '',
      'PÚBLICO ALVO': a.publico_alvo || '',
      ASSUNTO: a.assunto || '',
      'NOME (familiar)': a.nome_familiar || '',
      'NOME (inscrito)': a.nome_inscrito || '',
      'NOME (equipe)': a.nome_equipe || '',
      'NOME (fora do projeto)': a.nome_externo || '',
      OBSERVAÇÕES: a.observacoes || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(linhasExportacao);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ações Sociais');

    const hoje = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `Acoes_Sociais_${projetoSigla}_${hoje}.xlsx`);
    toast.success('Relatório gerado com sucesso!');
  };

  // --------------------------------------------------------------------------
  // MUTAÇÕES DO PRONTUÁRIO (PRESERVADAS 100%)
  // --------------------------------------------------------------------------

  const mutationAtendimento = useMutation({
    mutationFn: async (dados) => {
      return api.post(`/api/assistencia-social/${alunoProntuarioId}/atendimentos`, dados);
    },
    onSuccess: (res) => {
      toast.success(res?.message || 'Atendimento registrado com sucesso.');
      setModalAtendimentoAberto(false);
      setNovoAtendimento({
        tipo: 'presencial',
        data_atendimento: getTodayLocal(),
        descricao: '',
        encaminhamento: '',
        beneficio_concedido: '',
      });
      recarregarProntuario();
      queryClient.invalidateQueries({ queryKey: ['assistencia-social', institutionId] });
    },
    onError: (err) => {
      toast.error(err.message || 'Erro ao registrar atendimento.');
    },
  });

  const mutationExcluirAtendimento = useMutation({
    mutationFn: async (idAtendimento) => {
      return api.del(`/api/assistencia-social/atendimentos/${idAtendimento}`);
    },
    onSuccess: (res) => {
      toast.success(res?.message || 'Atendimento removido com sucesso.');
      recarregarProntuario();
      queryClient.invalidateQueries({ queryKey: ['assistencia-social', institutionId] });
    },
    onError: (err) => {
      toast.error(err.message || 'Erro ao remover atendimento.');
    },
  });

  const mutationSalvarPerfil = useMutation({
    mutationFn: async (dados) => {
      return api.put(`/api/assistencia-social/${alunoProntuarioId}/perfil`, dados);
    },
    onSuccess: (res) => {
      toast.success(res?.message || 'Perfil socioeconômico atualizado com sucesso.');
      setModalEditarPerfilAberto(false);
      recarregarProntuario();
      queryClient.invalidateQueries({ queryKey: ['assistencia-social', institutionId] });
    },
    onError: (err) => {
      toast.error(err.message || 'Erro ao atualizar perfil.');
    },
  });

  const mutationIncluirAluno = useMutation({
    mutationFn: async () => {
      if (!candidatoSelecionado) throw new Error('Selecione um aluno da instituição.');
      return api.post('/api/assistencia-social/incluir', {
        id_aluno: candidatoSelecionado.id,
        ...dadosInclusao,
      });
    },
    onSuccess: (res) => {
      toast.success(res?.message || 'Aluno incluído no acompanhamento social com sucesso.');
      setModalIncluirAberto(false);
      setCandidatoSelecionado(null);
      setBuscaCandidato('');
      setDadosInclusao({
        renda_familiar: '',
        moradores_casa: '',
        moradia_tipo: '',
        trabalho_responsaveis: '',
        recebe_beneficio: '',
        nis: '',
        vulnerabilidade: 'nao_informado',
        orgaos_acompanhamento: '',
        observacoes_social: '',
      });
      queryClient.invalidateQueries({ queryKey: ['assistencia-social', institutionId] });
      queryClient.invalidateQueries({ queryKey: ['candidatos-sociais', institutionId] });
    },
    onError: (err) => {
      toast.error(err.message || 'Erro ao incluir aluno no acompanhamento.');
    },
  });

  const mutationRemoverAluno = useMutation({
    mutationFn: async (idAluno) => {
      return api.del(`/api/assistencia-social/alunos/${idAluno}`);
    },
    onSuccess: (res) => {
      toast.success(res?.message || 'Aluno removido da lista de acompanhamento social.');
      setAlunoProntuarioId(null);
      queryClient.invalidateQueries({ queryKey: ['assistencia-social', institutionId] });
      queryClient.invalidateQueries({ queryKey: ['candidatos-sociais', institutionId] });
    },
    onError: (err) => {
      toast.error(err.message || 'Erro ao remover aluno.');
    },
  });

  const handleRemoverAlunoConfirmado = async (aluno) => {
    const ok = await confirm(
      `Deseja realmente remover "${aluno.nome}" da lista de acompanhamento social? O histórico de atendimentos e cadastros permanecerá salvo no banco de dados.`,
      { title: 'Remover do Acompanhamento Social', confirmText: 'Remover', tone: 'danger' },
    );
    if (!ok) return;
    mutationRemoverAluno.mutate(aluno.id);
  };

  const formatarDataBR = (dataIso) => {
    if (!dataIso) return '-';
    const partes = String(dataIso).split('T')[0].split('-');
    if (partes.length === 3) return `${partes[2]}/${partes[1]}/${partes[0]}`;
    return dataIso;
  };

  const formatarMoeda = (valor) => {
    if (valor === null || valor === undefined || isNaN(valor)) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
  };

  const alternarOrdenacao = (campo) => {
    setOrdenacao((prev) => {
      if (prev.campo === campo) {
        return { campo, direcao: prev.direcao === 'asc' ? 'desc' : 'asc' };
      }
      return { campo, direcao: 'asc' };
    });
  };

  // Filtragem e ordenação da tabela de alunos (Aba Prontuários)
  const alunosFiltrados = useMemo(() => {
    const filtrados = alunos.filter((a) => {
      const matchBusca =
        !busca.trim() ||
        a.nome.toLowerCase().includes(busca.toLowerCase()) ||
        (a.responsavel_nome && a.responsavel_nome.toLowerCase().includes(busca.toLowerCase())) ||
        (a.responsavel_bairro && a.responsavel_bairro.toLowerCase().includes(busca.toLowerCase())) ||
        (a.nis && a.nis.includes(busca));

      const matchVuln =
        filtroVulnerabilidade === 'todos' || a.vulnerabilidade === filtroVulnerabilidade;

      const temBeneficio =
        (a.recebe_beneficio && a.recebe_beneficio !== 'nenhum') || Boolean(a.nis && a.nis.trim());
      const matchBeneficio =
        filtroBeneficio === 'todos' ||
        (filtroBeneficio === 'com_beneficio' && temBeneficio) ||
        (filtroBeneficio === 'sem_beneficio' && !temBeneficio);

      const matchAlerta = filtroAlerta === 'todos' || (filtroAlerta === 'faltas' && a.faltas_recentes >= 3);

      return matchBusca && matchVuln && matchBeneficio && matchAlerta;
    });

    const { campo, direcao } = ordenacao;
    const mult = direcao === 'asc' ? 1 : -1;

    const PESO_VULNERABILIDADE = {
      critica: 4,
      alta: 3,
      media: 2,
      baixa: 1,
      nao_informado: 0,
    };

    return [...filtrados].sort((a, b) => {
      if (campo === 'nome') {
        return mult * (a.nome || '').localeCompare(b.nome || '', 'pt-BR');
      }
      if (campo === 'responsavel') {
        return mult * (a.responsavel_nome || '').localeCompare(b.responsavel_nome || '', 'pt-BR');
      }
      if (campo === 'renda') {
        const vA = a.renda_per_capita !== null && a.renda_per_capita !== undefined ? Number(a.renda_per_capita) : -1;
        const vB = b.renda_per_capita !== null && b.renda_per_capita !== undefined ? Number(b.renda_per_capita) : -1;
        return mult * (vA - vB);
      }
      if (campo === 'vulnerabilidade') {
        const pA = PESO_VULNERABILIDADE[a.vulnerabilidade] ?? 0;
        const pB = PESO_VULNERABILIDADE[b.vulnerabilidade] ?? 0;
        return mult * (pA - pB);
      }
      if (campo === 'beneficio') {
        const benA = a.recebe_beneficio || '';
        const benB = b.recebe_beneficio || '';
        return mult * benA.localeCompare(benB, 'pt-BR');
      }
      if (campo === 'atendimentos') {
        const atA = Number(a.total_atendimentos || 0);
        const atB = Number(b.total_atendimentos || 0);
        return mult * (atA - atB);
      }
      return 0;
    });
  }, [alunos, busca, filtroVulnerabilidade, filtroBeneficio, filtroAlerta, ordenacao]);

  // Lista de ações retornadas da API
  const listaAcoes = dadosAcoes?.acoes || [];

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 flex items-center justify-center text-rose-600 dark:text-rose-400">
              <HeartHandshake className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                Serviço Social & Acompanhamento Familiar
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                Diário de ações, atendimentos, visitas e diagnósticos socioeconômicos integrados
              </p>
            </div>
          </div>
        </div>

        {/* Botões de Ação no Topo dependendo da aba ativa */}
        <div className="flex flex-wrap items-center gap-2">
          {abaPrincipal === 'diario' ? (
            <>
              {podeExportar && (
                <Button
                  variant="secondary"
                  icon={Download}
                  onClick={exportarExcelAcoes}
                  disabled={!listaAcoes.length}
                >
                  Exportar Planilha
                </Button>
              )}
              {podeCriar && (
                <Button
                  variant="primary"
                  icon={Plus}
                  className="!bg-rose-600 hover:!bg-rose-700"
                  onClick={() => abrirModalNovaAcao()}
                >
                  Nova Ação / Atendimento
                </Button>
              )}
            </>
          ) : (
            podeCriar && (
              <Button
                variant="primary"
                icon={UserPlus}
                onClick={() => {
                  setCandidatoSelecionado(null);
                  setBuscaCandidato('');
                  setModalIncluirAberto(true);
                }}
              >
                Incluir Aluno no Acompanhamento
              </Button>
            )
          )}
        </div>
      </div>

      {/* Navegação por Abas Principais */}
      <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-800 pb-2">
        <button
          type="button"
          onClick={() => setAbaPrincipal('diario')}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
            abaPrincipal === 'diario'
              ? 'bg-rose-600 text-white shadow-sm'
              : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 border border-gray-200 dark:border-gray-800'
          }`}
        >
          <ClipboardList className="w-4 h-4" />
          Diário de Atividades / Ações
          {dadosAcoes?.total > 0 && (
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                abaPrincipal === 'diario'
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              }`}
            >
              {dadosAcoes.total}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setAbaPrincipal('prontuarios')}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
            abaPrincipal === 'prontuarios'
              ? 'bg-rose-600 text-white shadow-sm'
              : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 border border-gray-200 dark:border-gray-800'
          }`}
        >
          <Users className="w-4 h-4" />
          Prontuários dos Inscritos
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-bold ${
              abaPrincipal === 'prontuarios'
                ? 'bg-white/20 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            }`}
          >
            {alunos.length}
          </span>
        </button>
      </div>

      {/* ==================================================================== */}
      {/* ABA 1: DIÁRIO DE AÇÕES / ATENDIMENTOS (PLANILHA INTEGRADA)          */}
      {/* ==================================================================== */}
      {abaPrincipal === 'diario' && (
        <div className="space-y-4">
          {/* Cards de Resumo das Ações */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="p-3.5 rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Total de Ações
              </span>
              <div className="mt-1">
                <span className="text-2xl font-extrabold text-gray-900 dark:text-white">
                  {dadosAcoes?.total || 0}
                </span>
              </div>
              <p className="text-[10px] text-gray-400 mt-0.5">registros no sistema</p>
            </div>

            <div className="p-3.5 rounded-2xl bg-white dark:bg-gray-900 border border-blue-100 dark:border-blue-900/30 shadow-sm flex flex-col justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                Atendimentos
              </span>
              <div className="mt-1">
                <span className="text-2xl font-extrabold text-blue-700 dark:text-blue-300">
                  {Object.entries(dadosAcoes?.resumo?.porAtividade || {}).reduce(
                    (acc, [k, v]) => (k.toLowerCase().includes('atendimento') ? acc + v : acc),
                    0,
                  )}
                </span>
              </div>
              <p className="text-[10px] text-blue-500/80 mt-0.5">alunos, famílias e equipe</p>
            </div>

            <div className="p-3.5 rounded-2xl bg-white dark:bg-gray-900 border border-purple-100 dark:border-purple-900/30 shadow-sm flex flex-col justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400">
                Visitas Sociais
              </span>
              <div className="mt-1">
                <span className="text-2xl font-extrabold text-purple-700 dark:text-purple-300">
                  {Object.entries(dadosAcoes?.resumo?.porAtividade || {}).reduce(
                    (acc, [k, v]) => (k.toLowerCase().includes('visita') ? acc + v : acc),
                    0,
                  )}
                </span>
              </div>
              <p className="text-[10px] text-purple-500/80 mt-0.5">domiciliares e técnicas</p>
            </div>

            <div className="p-3.5 rounded-2xl bg-white dark:bg-gray-900 border border-emerald-100 dark:border-emerald-900/30 shadow-sm flex flex-col justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                Reuniões & Redes
              </span>
              <div className="mt-1">
                <span className="text-2xl font-extrabold text-emerald-700 dark:text-emerald-300">
                  {Object.entries(dadosAcoes?.resumo?.porAtividade || {}).reduce(
                    (acc, [k, v]) => (k.toLowerCase().includes('reuni') ? acc + v : acc),
                    0,
                  )}
                </span>
              </div>
              <p className="text-[10px] text-emerald-500/80 mt-0.5">CRAS, escolas e conselho</p>
            </div>

            <div className="p-3.5 rounded-2xl bg-white dark:bg-gray-900 border border-amber-100 dark:border-amber-900/30 shadow-sm flex flex-col justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                Relatórios & Pareceres
              </span>
              <div className="mt-1">
                <span className="text-2xl font-extrabold text-amber-700 dark:text-amber-300">
                  {Object.entries(dadosAcoes?.resumo?.porAtividade || {}).reduce(
                    (acc, [k, v]) =>
                      k.toLowerCase().includes('relat') ||
                      k.toLowerCase().includes('document') ||
                      k.toLowerCase().includes('ofício')
                        ? acc + v
                        : acc,
                    0,
                  )}
                </span>
              </div>
              <p className="text-[10px] text-amber-500/80 mt-0.5">documentos elaborados</p>
            </div>
          </div>

          {/* Barra de Filtros do Diário */}
          <Card className="p-3.5 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Pesquisar por aluno, familiar, equipe..."
                  value={filtrosAcoes.busca}
                  onChange={(e) => setFiltrosAcoes((prev) => ({ ...prev, busca: e.target.value }))}
                  className="w-full pl-9 pr-3 py-2 text-xs rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-rose-500/30"
                />
              </div>

              <Select
                value={filtrosAcoes.atividade}
                onChange={(e) => setFiltrosAcoes((prev) => ({ ...prev, atividade: e.target.value }))}
                options={[
                  { value: 'todos', label: 'Todas as Atividades' },
                  ...ATIVIDADES_PADRAO.map((atv) => ({ value: atv, label: atv })),
                ]}
              />

              <Select
                value={filtrosAcoes.assunto}
                onChange={(e) => setFiltrosAcoes((prev) => ({ ...prev, assunto: e.target.value }))}
                options={[
                  { value: 'todos', label: 'Todos os Assuntos' },
                  ...ASSUNTOS_PADRAO.map((ass) => ({ value: ass, label: ass })),
                ]}
              />

              <Select
                value={filtrosAcoes.publico_alvo}
                onChange={(e) =>
                  setFiltrosAcoes((prev) => ({ ...prev, publico_alvo: e.target.value }))
                }
                options={[
                  { value: 'todos', label: 'Todos os Públicos' },
                  ...PUBLICOS_ALVO_PADRAO.map((pub) => ({ value: pub, label: pub })),
                ]}
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-gray-100 dark:border-gray-800 text-xs">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-500 dark:text-gray-400 text-xs">De:</span>
                  <input
                    type="date"
                    value={filtrosAcoes.data_inicio}
                    onChange={(e) =>
                      setFiltrosAcoes((prev) => ({ ...prev, data_inicio: e.target.value }))
                    }
                    className="px-2 py-1 text-xs rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-500 dark:text-gray-400 text-xs">Até:</span>
                  <input
                    type="date"
                    value={filtrosAcoes.data_fim}
                    onChange={(e) =>
                      setFiltrosAcoes((prev) => ({ ...prev, data_fim: e.target.value }))
                    }
                    className="px-2 py-1 text-xs rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                {opcoesFiltros?.assistentes?.length > 1 && (
                  <Select
                    value={filtrosAcoes.assistente}
                    onChange={(e) =>
                      setFiltrosAcoes((prev) => ({ ...prev, assistente: e.target.value }))
                    }
                    options={[
                      { value: 'todos', label: 'Todas as Assistentes' },
                      ...opcoesFiltros.assistentes.map((as) => ({ value: as, label: as })),
                    ]}
                  />
                )}
              </div>

              {(filtrosAcoes.busca ||
                filtrosAcoes.atividade !== 'todos' ||
                filtrosAcoes.assunto !== 'todos' ||
                filtrosAcoes.publico_alvo !== 'todos' ||
                filtrosAcoes.data_inicio ||
                filtrosAcoes.data_fim ||
                filtrosAcoes.assistente !== 'todos') && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setFiltrosAcoes({
                      busca: '',
                      data_inicio: '',
                      data_fim: '',
                      atividade: 'todos',
                      publico_alvo: 'todos',
                      assunto: 'todos',
                      assistente: 'todos',
                    })
                  }
                >
                  Limpar Filtros
                </Button>
              )}
            </div>
          </Card>

          {/* Tabela de Ações Sociais */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm">
            {carregandoAcoes ? (
              <div className="p-12 text-center text-gray-400">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-rose-500" />
                <p className="text-xs">Carregando ações do serviço social...</p>
              </div>
            ) : !listaAcoes.length ? (
              <div className="p-12 text-center text-gray-400">
                <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-30 text-rose-500" />
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Nenhuma ação social encontrada
                </p>
                <p className="text-xs text-gray-400 mt-1 max-w-md mx-auto">
                  Utilize o botão &quot;Nova Ação / Atendimento&quot; para registrar visitas, reuniões,
                  atendimentos e pareceres do Serviço Social.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/40 text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">
                      <th className="py-3 px-3.5">Data</th>
                      <th className="py-3 px-3.5">Atividade</th>
                      <th className="py-3 px-3.5">Público / Assunto</th>
                      <th className="py-3 px-3.5">Pessoas Envolvidas</th>
                      <th className="py-3 px-3.5">Assistente Social</th>
                      <th className="py-3 px-3.5">Observações / Relato</th>
                      <th className="py-3 px-3.5 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {listaAcoes.map((acao) => {
                      const expandido = observacaoExpandidaId === acao.id;
                      const textoObs = acao.observacoes || '';
                      const ehLongo = textoObs.length > 80;

                      return (
                        <tr
                          key={acao.id}
                          className="hover:bg-gray-50/60 dark:hover:bg-gray-800/30 transition-colors"
                        >
                          {/* Data */}
                          <td className="py-3 px-3.5 whitespace-nowrap font-medium text-gray-900 dark:text-gray-100">
                            {formatarDataBR(acao.data_acao)}
                          </td>

                          {/* Atividade */}
                          <td className="py-3 px-3.5">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800">
                              {acao.atividade}
                            </span>
                          </td>

                          {/* Público / Assunto */}
                          <td className="py-3 px-3.5 space-y-1">
                            {acao.publico_alvo && (
                              <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300">
                                {acao.publico_alvo}
                              </div>
                            )}
                            {acao.assunto && (
                              <div className="text-[10px] text-gray-500 dark:text-gray-400">
                                {acao.assunto}
                              </div>
                            )}
                          </td>

                          {/* Pessoas Envolvidas */}
                          <td className="py-3 px-3.5 space-y-1 max-w-xs">
                            {acao.nome_inscrito && (
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] uppercase font-bold text-blue-600 dark:text-blue-400">
                                  Inscrito:
                                </span>
                                {acao.id_aluno ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setAlunoProntuarioId(acao.id_aluno);
                                      setAbaProntuario('atendimentos');
                                    }}
                                    className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 truncate"
                                    title="Abrir prontuário do aluno"
                                  >
                                    <span className="truncate">{acao.nome_inscrito}</span>
                                    <ExternalLink className="w-3 h-3 shrink-0" />
                                  </button>
                                ) : (
                                  <span className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
                                    {acao.nome_inscrito}
                                  </span>
                                )}
                              </div>
                            )}

                            {acao.nome_familiar && (
                              <div className="text-[11px] text-gray-700 dark:text-gray-300 truncate">
                                <span className="font-bold text-gray-500 dark:text-gray-400">Família:</span>{' '}
                                {acao.nome_familiar}
                              </div>
                            )}

                            {acao.nome_equipe && (
                              <div className="text-[11px] text-gray-700 dark:text-gray-300 truncate">
                                <span className="font-bold text-gray-500 dark:text-gray-400">Equipe:</span>{' '}
                                {acao.nome_equipe}
                              </div>
                            )}

                            {acao.nome_externo && (
                              <div className="text-[11px] text-purple-700 dark:text-purple-300 truncate">
                                <span className="font-bold text-purple-500">Externo:</span>{' '}
                                {acao.nome_externo}
                              </div>
                            )}

                            {!acao.nome_inscrito &&
                              !acao.nome_familiar &&
                              !acao.nome_equipe &&
                              !acao.nome_externo && (
                                <span className="text-gray-400 text-[11px] italic">Geral / Não especificado</span>
                              )}
                          </td>

                          {/* Assistente Social */}
                          <td className="py-3 px-3.5 whitespace-nowrap text-gray-700 dark:text-gray-300 font-medium">
                            {acao.nome_assistente_social || '-'}
                          </td>

                          {/* Observações / Relato */}
                          <td className="py-3 px-3.5 max-w-sm">
                            {textoObs ? (
                              <div>
                                <p
                                  className={`text-[11px] text-gray-700 dark:text-gray-300 whitespace-pre-wrap ${
                                    !expandido && ehLongo ? 'line-clamp-2' : ''
                                  }`}
                                >
                                  {textoObs}
                                </p>
                                {ehLongo && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setObservacaoExpandidaId(expandido ? null : acao.id)
                                    }
                                    className="text-[10px] text-rose-600 dark:text-rose-400 font-semibold mt-0.5 inline-flex items-center gap-0.5"
                                  >
                                    {expandido ? (
                                      <>
                                        Menos <ChevronUp className="w-3 h-3" />
                                      </>
                                    ) : (
                                      <>
                                        Ver mais <ChevronDown className="w-3 h-3" />
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-[11px] italic">Sem observações</span>
                            )}
                          </td>

                          {/* Ações */}
                          <td className="py-3 px-3.5 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1">
                              {podeEditar && (
                                <button
                                  type="button"
                                  onClick={() => abrirModalEditarAcao(acao)}
                                  className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                  title="Editar ação"
                                >
                                  <Edit3 className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {podeExcluir && (
                                <button
                                  type="button"
                                  onClick={() => handleExcluirAcaoConfirmada(acao)}
                                  className="p-1.5 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                                  title="Excluir ação"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* ABA 2: PRONTUÁRIOS DOS INSCRITOS (TELA EXISTENTE PRESERVADA 100%)    */}
      {/* ==================================================================== */}
      {abaPrincipal === 'prontuarios' && (
        <div className="space-y-6">
          {/* Cards de Indicadores / Resumo dos Alunos */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            <div className="p-4 rounded-3xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col justify-between">
              <div className="flex items-center justify-between text-gray-400">
                <span className="text-xs font-semibold uppercase tracking-wider">Acompanhadas</span>
                <Users className="w-4 h-4 text-blue-500" />
              </div>
              <div className="mt-2">
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  {resumo.total_com_acompanhamento}
                </span>
                <span className="text-xs text-gray-400 ml-1.5">de {resumo.total_alunos}</span>
              </div>
              <p className="text-[11px] text-gray-400 mt-1">com perfil ou atendimento</p>
            </div>

            <div className="p-4 rounded-3xl bg-white dark:bg-gray-900 border border-red-100 dark:border-red-900/30 shadow-sm flex flex-col justify-between">
              <div className="flex items-center justify-between text-red-500">
                <span className="text-xs font-semibold uppercase tracking-wider">Alta / Crítica</span>
                <ShieldAlert className="w-4 h-4" />
              </div>
              <div className="mt-2">
                <span className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {resumo.total_critica + resumo.total_alta}
                </span>
                <span className="text-xs text-red-400 ml-1.5">({resumo.total_critica} críticas)</span>
              </div>
              <p className="text-[11px] text-red-400 mt-1">requerem atenção prioritária</p>
            </div>

            <div className="p-4 rounded-3xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col justify-between">
              <div className="flex items-center justify-between text-gray-400">
                <span className="text-xs font-semibold uppercase tracking-wider">Benefícios</span>
                <DollarSign className="w-4 h-4 text-emerald-500" />
              </div>
              <div className="mt-2">
                <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  {resumo.total_bolsa_familia}
                </span>
              </div>
              <p className="text-[11px] text-gray-400 mt-1">Bolsa Família / NIS ativo</p>
            </div>

            <div className="p-4 rounded-3xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col justify-between">
              <div className="flex items-center justify-between text-gray-400">
                <span className="text-xs font-semibold uppercase tracking-wider">Alerta Faltas</span>
                <AlertTriangle className="w-4 h-4 text-amber-500" />
              </div>
              <div className="mt-2">
                <span className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                  {resumo.total_alerta_faltas}
                </span>
              </div>
              <p className="text-[11px] text-gray-400 mt-1">3+ faltas nos últimos 30 dias</p>
            </div>

            <div className="p-4 rounded-3xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col justify-between">
              <div className="flex items-center justify-between text-gray-400">
                <span className="text-xs font-semibold uppercase tracking-wider">No Mês</span>
                <MessageSquare className="w-4 h-4 text-rose-500" />
              </div>
              <div className="mt-2">
                <span className="text-2xl font-bold text-rose-600 dark:text-rose-400">
                  {resumo.atendimentos_mes}
                </span>
              </div>
              <p className="text-[11px] text-gray-400 mt-1">ações/atendimentos este mês</p>
            </div>
          </div>

          {/* Barra de Busca e Filtros dos Prontuários */}
          <div className="p-4 rounded-3xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col md:flex-row gap-3 items-center justify-between">
            <div className="w-full md:w-80 relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Buscar por aluno, responsável, bairro ou NIS..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-rose-500/20"
              />
            </div>

            <div className="w-full md:w-auto flex flex-wrap gap-2 items-center">
              <Select
                value={filtroVulnerabilidade}
                onChange={(e) => setFiltroVulnerabilidade(e.target.value)}
                options={[
                  { value: 'todos', label: 'Vulnerabilidade: Todas' },
                  { value: 'critica', label: 'Crítica' },
                  { value: 'alta', label: 'Alta' },
                  { value: 'media', label: 'Média' },
                  { value: 'baixa', label: 'Baixa' },
                  { value: 'nao_informado', label: 'Não Informada' },
                ]}
              />

              <Select
                value={filtroBeneficio}
                onChange={(e) => setFiltroBeneficio(e.target.value)}
                options={[
                  { value: 'todos', label: 'Benefícios: Todos' },
                  { value: 'com_beneficio', label: 'Com Benefício / NIS' },
                  { value: 'sem_beneficio', label: 'Sem Benefício' },
                ]}
              />

              <Select
                value={filtroAlerta}
                onChange={(e) => setFiltroAlerta(e.target.value)}
                options={[
                  { value: 'todos', label: 'Alertas: Todos' },
                  { value: 'faltas', label: 'Alerta de Faltas (3+)' },
                ]}
              />
            </div>
          </div>

          {/* Tabela de Alunos / Prontuários */}
          <div className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 overflow-hidden shadow-sm">
            {isLoading ? (
              <div className="p-12 text-center text-gray-400">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-rose-500" />
                <p className="text-xs">Carregando dados da assistência social...</p>
              </div>
            ) : !alunosFiltrados.length ? (
              <div className="p-12 text-center text-gray-400">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">Nenhum aluno encontrado com os filtros aplicados.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50 text-gray-400 uppercase tracking-wider font-semibold">
                      <th className="py-3 px-4">
                        <button
                          type="button"
                          onClick={() => alternarOrdenacao('nome')}
                          className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors uppercase font-semibold"
                        >
                          Aluno
                          {ordenacao.campo === 'nome' ? (
                            ordenacao.direcao === 'asc' ? (
                              <ArrowUp className="w-3 h-3 text-rose-500" />
                            ) : (
                              <ArrowDown className="w-3 h-3 text-rose-500" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3 h-3 opacity-40" />
                          )}
                        </button>
                      </th>
                      <th className="py-3 px-4">
                        <button
                          type="button"
                          onClick={() => alternarOrdenacao('responsavel')}
                          className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors uppercase font-semibold"
                        >
                          Responsável & Contato
                          {ordenacao.campo === 'responsavel' ? (
                            ordenacao.direcao === 'asc' ? (
                              <ArrowUp className="w-3 h-3 text-rose-500" />
                            ) : (
                              <ArrowDown className="w-3 h-3 text-rose-500" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3 h-3 opacity-40" />
                          )}
                        </button>
                      </th>
                      <th className="py-3 px-4">
                        <button
                          type="button"
                          onClick={() => alternarOrdenacao('renda')}
                          className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors uppercase font-semibold"
                        >
                          Situação Social
                          {ordenacao.campo === 'renda' ? (
                            ordenacao.direcao === 'asc' ? (
                              <ArrowUp className="w-3 h-3 text-rose-500" />
                            ) : (
                              <ArrowDown className="w-3 h-3 text-rose-500" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3 h-3 opacity-40" />
                          )}
                        </button>
                      </th>
                      <th className="py-3 px-4">
                        <button
                          type="button"
                          onClick={() => alternarOrdenacao('vulnerabilidade')}
                          className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors uppercase font-semibold"
                        >
                          Vulnerabilidade
                          {ordenacao.campo === 'vulnerabilidade' ? (
                            ordenacao.direcao === 'asc' ? (
                              <ArrowUp className="w-3 h-3 text-rose-500" />
                            ) : (
                              <ArrowDown className="w-3 h-3 text-rose-500" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3 h-3 opacity-40" />
                          )}
                        </button>
                      </th>
                      <th className="py-3 px-4">
                        <button
                          type="button"
                          onClick={() => alternarOrdenacao('atendimentos')}
                          className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors uppercase font-semibold"
                        >
                          Histórico
                          {ordenacao.campo === 'atendimentos' ? (
                            ordenacao.direcao === 'asc' ? (
                              <ArrowUp className="w-3 h-3 text-rose-500" />
                            ) : (
                              <ArrowDown className="w-3 h-3 text-rose-500" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3 h-3 opacity-40" />
                          )}
                        </button>
                      </th>
                      <th className="py-3 px-4 text-right">Ação</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {alunosFiltrados.map((aluno) => {
                      const cv = CORES_VULNERABILIDADE[aluno.vulnerabilidade] || CORES_VULNERABILIDADE.nao_informado;
                      const temAlertaFaltas = aluno.faltas_recentes >= 3;

                      return (
                        <tr
                          key={aluno.id}
                          className="hover:bg-gray-50/50 dark:hover:bg-gray-800/40 transition-colors cursor-pointer"
                          onClick={() => {
                            setAlunoProntuarioId(aluno.id);
                            setAbaProntuario('diagnostico');
                          }}
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <AvatarAluno fotoUrl={aluno.foto_url} nome={aluno.nome} tamanho="sm" />
                              <div className="min-w-0">
                                <span className="font-semibold text-gray-900 dark:text-white block truncate">
                                  {aluno.nome}
                                </span>
                                <span className="text-[11px] text-gray-400">
                                  Turma: {aluno.turma || '—'} · Turno: {aluno.turno || '—'}
                                </span>
                              </div>
                            </div>
                          </td>

                          <td className="py-3 px-4">
                            <div>
                              <span className="font-medium text-gray-800 dark:text-gray-200 block">
                                {aluno.responsavel_nome || 'Não informado'}
                              </span>
                              <div className="flex items-center gap-2 text-[11px] text-gray-400 mt-0.5">
                                {aluno.responsavel_telefone && (
                                  <span className="flex items-center gap-0.5">
                                    <Phone className="w-3 h-3 text-gray-400" />
                                    {aluno.responsavel_telefone}
                                  </span>
                                )}
                                {aluno.responsavel_bairro && (
                                  <span className="flex items-center gap-0.5">
                                    <MapPin className="w-3 h-3 text-gray-400" />
                                    {aluno.responsavel_bairro}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          <td className="py-3 px-4">
                            <div className="space-y-0.5">
                              {aluno.renda_per_capita !== null ? (
                                <span className="text-gray-700 dark:text-gray-300 font-medium block">
                                  {formatarMoeda(aluno.renda_per_capita)}{' '}
                                  <span className="text-[10px] text-gray-400 font-normal">/ pessoa</span>
                                </span>
                              ) : (
                                <span className="text-gray-400 text-[11px]">Renda não informada</span>
                              )}
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {aluno.recebe_beneficio && aluno.recebe_beneficio !== 'nenhum' && (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 font-medium border border-emerald-200 dark:border-emerald-800">
                                    {ROTULOS_BENEFICIO[aluno.recebe_beneficio] || aluno.recebe_beneficio}
                                  </span>
                                )}
                                {aluno.nis && (
                                  <span className="text-[10px] text-gray-400">NIS: {aluno.nis}</span>
                                )}
                              </div>
                            </div>
                          </td>

                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${cv.badge}`}
                            >
                              {cv.label}
                            </span>
                          </td>

                          <td className="py-3 px-4">
                            <div className="space-y-0.5">
                              <span className="text-gray-700 dark:text-gray-300 font-medium block">
                                {aluno.total_atendimentos}{' '}
                                <span className="text-gray-400 font-normal text-[11px]">
                                  {aluno.total_atendimentos === 1 ? 'registro' : 'registros'}
                                </span>
                              </span>
                              {temAlertaFaltas && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                                  <AlertTriangle className="w-3 h-3" />
                                  {aluno.faltas_recentes} faltas no mês
                                </span>
                              )}
                            </div>
                          </td>

                          <td className="py-3 px-4 text-right">
                            <Button
                              variant="secondary"
                              size="sm"
                              icon={FileText}
                              onClick={(e) => {
                                e.stopPropagation();
                                setAlunoProntuarioId(aluno.id);
                                setAbaProntuario('diagnostico');
                              }}
                            >
                              Prontuário
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* MODAL: REGISTRAR / EDITAR AÇÃO SOCIAL (NOVO - DIÁRIO DE BORDO)       */}
      {/* ==================================================================== */}
      <Modal
        isOpen={modalAcaoAberto}
        onClose={() => {
          setModalAcaoAberto(false);
          setAcaoEmEdicao(null);
        }}
        title={acaoEmEdicao ? 'Editar Registro de Ação Social' : 'Nova Ação / Atendimento do Serviço Social'}
        maxWidth="5xl"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutationSalvarAcao.mutate(formAcao);
          }}
          className="space-y-5"
        >
          {/* Seção 1: Identificação & Classificação */}
          <div className="space-y-3">
            <div className="flex items-center justify-between pb-1 border-b border-gray-100 dark:border-gray-800">
              <span className="text-xs font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" />
                Classificação da Ação
              </span>
              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                Campos com * são de preenchimento obrigatório
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
              <Input
                type="date"
                label="Data da Ação *"
                value={formAcao.data_acao}
                onChange={(e) => setFormAcao({ ...formAcao, data_acao: e.target.value })}
                required
              />

              <Input
                type="text"
                label="Assistente Social *"
                value={formAcao.nome_assistente_social}
                onChange={(e) => setFormAcao({ ...formAcao, nome_assistente_social: e.target.value })}
                placeholder="Nome da profissional"
                required
              />

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Atividade *
                </label>
                <select
                  value={formAcao.atividade}
                  onChange={(e) => setFormAcao({ ...formAcao, atividade: e.target.value })}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                  required
                >
                  {ATIVIDADES_PADRAO.map((atv) => (
                    <option key={atv} value={atv}>
                      {atv}
                    </option>
                  ))}
                </select>
              </div>

              <Select
                label="Público Alvo"
                value={formAcao.publico_alvo}
                onChange={(e) => setFormAcao({ ...formAcao, publico_alvo: e.target.value })}
                options={PUBLICOS_ALVO_PADRAO.map((pub) => ({ value: pub, label: pub }))}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
              <Select
                label="Assunto / Vulnerabilidade Prioritária"
                value={formAcao.assunto}
                onChange={(e) => setFormAcao({ ...formAcao, assunto: e.target.value })}
                options={ASSUNTOS_PADRAO.map((ass) => ({ value: ass, label: ass }))}
              />

              <div className="flex flex-col justify-end">
                <div className="px-3.5 py-2 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60 flex items-center justify-between text-xs">
                  <span className="text-gray-500 dark:text-gray-400">Unidade Vinculada:</span>
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {Number(institutionId) === 1
                      ? 'Conexão Sertão (CSertão)'
                      : Number(institutionId) === 2
                      ? 'Conexão Campo Largo (CCLargo)'
                      : 'Instituição de Testes (ID 3)'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Seção 2: Pessoas & Rede Envolvida */}
          <div className="p-4 rounded-2xl bg-gray-50/70 dark:bg-gray-800/30 border border-gray-200/70 dark:border-gray-800/80 space-y-3.5">
            <div className="flex items-center justify-between pb-1 border-b border-gray-200/60 dark:border-gray-800">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-rose-500" />
                Pessoas Envolvidas & Articulação de Rede
              </span>
              <span className="text-[11px] text-gray-400 dark:text-gray-500">
                Preencha os campos aplicáveis à intervenção
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Coluna 1: Aluno & Família */}
              <div className="space-y-3 p-3 rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-xs">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Aluno Inscrito (Vínculo Opcional ao Prontuário)
                  </label>
                  <select
                    value={formAcao.id_aluno}
                    onChange={(e) => {
                      const selId = e.target.value;
                      const alunoSel = listaAlunosInst.find((a) => String(a.id) === selId);
                      setFormAcao((prev) => ({
                        ...prev,
                        id_aluno: selId,
                        nome_inscrito: alunoSel ? alunoSel.nome : prev.nome_inscrito,
                        nome_familiar:
                          alunoSel?.responsavel_nome && !prev.nome_familiar
                            ? alunoSel.responsavel_nome
                            : prev.nome_familiar,
                      }));
                    }}
                    className="w-full px-3 py-2 text-xs rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                  >
                    <option value="">Ação Geral (Nenhum aluno específico vinculado)</option>
                    {listaAlunosInst.map((al) => (
                      <option key={al.id} value={al.id}>
                        {al.nome} {al.turma ? `— Turma ${al.turma}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <Input
                  label="Nome do Familiar / Responsável"
                  value={formAcao.nome_familiar}
                  onChange={(e) => setFormAcao({ ...formAcao, nome_familiar: e.target.value })}
                  placeholder="Ex: Solange (avó), Aline (mãe), etc."
                />
              </div>

              {/* Coluna 2: Equipe Interna & Rede Externa */}
              <div className="space-y-3 p-3 rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-xs">
                <Input
                  label="Integrante da Equipe Interna"
                  value={formAcao.nome_equipe}
                  onChange={(e) => setFormAcao({ ...formAcao, nome_equipe: e.target.value })}
                  placeholder="Ex: Alessandra Moraes, Fabíola, Educadores..."
                />

                <Input
                  label="Entidade ou Profissional Fora do Projeto"
                  value={formAcao.nome_externo}
                  onChange={(e) => setFormAcao({ ...formAcao, nome_externo: e.target.value })}
                  placeholder="Ex: CRAS Ferraria, CREAS, Conselho Tutelar, Escola, Posto..."
                />
              </div>
            </div>
          </div>

          {/* Seção 3: Relato Técnico */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Observações / Relato Técnico & Encaminhamentos
            </label>
            <textarea
              rows={4}
              value={formAcao.observacoes}
              onChange={(e) => setFormAcao({ ...formAcao, observacoes: e.target.value })}
              placeholder="Descreva a escuta qualificada, orientações passadas, encaminhamentos e desfechos..."
              className="w-full px-3.5 py-2.5 text-xs rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-rose-500/20 leading-relaxed"
            />
          </div>

          {/* Rodapé de Ações */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setModalAcaoAberto(false);
                setAcaoEmEdicao(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={mutationSalvarAcao.isPending}
              icon={Save}
            >
              {mutationSalvarAcao.isPending
                ? 'Salvando...'
                : acaoEmEdicao
                ? 'Atualizar Ação Social'
                : 'Salvar Ação Social'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ==================================================================== */}
      {/* MODAL: PRONTUÁRIO SOCIAL DO ALUNO (PRESERVADO 100% E EXPANDIDO)       */}
      {/* ==================================================================== */}
      {alunoProntuarioId && (
        <Modal
          isOpen={!!alunoProntuarioId}
          onClose={() => setAlunoProntuarioId(null)}
          title="Prontuário Social Integrado"
          maxWidth="2xl"
        >
          {carregandoProntuario || !prontuario ? (
            <div className="p-12 text-center text-gray-400">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-rose-500" />
              <p className="text-xs">Carregando prontuário...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Card Resumo do Aluno no Topo do Prontuário */}
              <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <AvatarAluno fotoUrl={prontuario.foto_url} nome={prontuario.nome} tamanho="lg" />
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-gray-900 dark:text-white text-base">
                        {prontuario.nome}
                      </h3>
                      {prontuario.bolsista ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Bolsista
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatarIdade(null, prontuario.data_nascimento)} · Turma: {prontuario.turma || '—'} (
                      {prontuario.turno || '—'})
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Responsável: {prontuario.responsavel_nome || 'Não informado'}{' '}
                      {prontuario.responsavel_telefone ? `(${prontuario.responsavel_telefone})` : ''}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto justify-end">
                  {podeCriar && (
                    <Button
                      variant="primary"
                      size="sm"
                      icon={Plus}
                      className="!bg-rose-600 hover:!bg-rose-700"
                      onClick={() => abrirModalNovaAcao(prontuario)}
                    >
                      Nova Ação Social
                    </Button>
                  )}
                  {podeExcluir && (
                    <Button
                      variant="danger"
                      size="sm"
                      icon={UserMinus}
                      onClick={() => handleRemoverAlunoConfirmado(prontuario)}
                    >
                      Desvincular
                    </Button>
                  )}
                </div>
              </div>

              {/* Sub-abas do Prontuário */}
              <div className="flex border-b border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setAbaProntuario('diagnostico')}
                  className={`pb-3 px-4 text-xs font-semibold border-b-2 transition-colors ${
                    abaProntuario === 'diagnostico'
                      ? 'border-rose-500 text-rose-600 dark:text-rose-400'
                      : 'border-transparent text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Diagnóstico Sociodemográfico
                </button>
                <button
                  type="button"
                  onClick={() => setAbaProntuario('atendimentos')}
                  className={`pb-3 px-4 text-xs font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
                    abaProntuario === 'atendimentos'
                      ? 'border-rose-500 text-rose-600 dark:text-rose-400'
                      : 'border-transparent text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Histórico de Ações & Atendimentos
                  <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 font-bold">
                    {prontuario.atendimentos?.length || 0}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setAbaProntuario('alertas')}
                  className={`pb-3 px-4 text-xs font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
                    abaProntuario === 'alertas'
                      ? 'border-rose-500 text-rose-600 dark:text-rose-400'
                      : 'border-transparent text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Alertas, Faltas & Ocorrências
                  {prontuario.faltas_recentes?.length >= 3 && (
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                  )}
                </button>
              </div>

              {/* Sub-aba: Diagnóstico Sociodemográfico */}
              {abaProntuario === 'diagnostico' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                      Perfil Familiar e Socioeconômico
                    </span>
                    {podeEditar && (
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={Edit3}
                        onClick={() => {
                          setPerfilEditavel({
                            renda_familiar: prontuario.renda_familiar || '',
                            moradores_casa: prontuario.moradores_casa || '',
                            moradia_tipo: prontuario.moradia_tipo || '',
                            trabalho_responsaveis: prontuario.trabalho_responsaveis || '',
                            recebe_beneficio: prontuario.recebe_beneficio || '',
                            nis: prontuario.nis || '',
                            vulnerabilidade: prontuario.vulnerabilidade || 'nao_informado',
                            orgaos_acompanhamento: prontuario.orgaos_acompanhamento || '',
                            observacoes_social: prontuario.observacoes_social || '',
                          });
                          setModalEditarPerfilAberto(true);
                        }}
                      >
                        Editar Perfil
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800">
                      <span className="text-[10px] uppercase font-bold text-gray-400 block">Renda Total</span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5 block">
                        {formatarMoeda(prontuario.renda_familiar)}
                      </span>
                    </div>

                    <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800">
                      <span className="text-[10px] uppercase font-bold text-gray-400 block">Moradores</span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5 block">
                        {prontuario.moradores_casa ? `${prontuario.moradores_casa} pessoas` : 'Não informado'}
                      </span>
                    </div>

                    <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800">
                      <span className="text-[10px] uppercase font-bold text-gray-400 block">Renda per Capita</span>
                      <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 mt-0.5 block">
                        {formatarMoeda(prontuario.renda_per_capita)}
                      </span>
                    </div>

                    <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800">
                      <span className="text-[10px] uppercase font-bold text-gray-400 block">Moradia</span>
                      <span className="text-xs font-semibold text-gray-900 dark:text-white mt-0.5 block">
                        {ROTULOS_MORADIA[prontuario.moradia_tipo] || prontuario.moradia_tipo || 'Não informado'}
                      </span>
                    </div>

                    <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800">
                      <span className="text-[10px] uppercase font-bold text-gray-400 block">Trabalho</span>
                      <span className="text-xs font-semibold text-gray-900 dark:text-white mt-0.5 block">
                        {ROTULOS_TRABALHO[prontuario.trabalho_responsaveis] ||
                          prontuario.trabalho_responsaveis ||
                          'Não informado'}
                      </span>
                    </div>

                    <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800">
                      <span className="text-[10px] uppercase font-bold text-gray-400 block">Benefício / NIS</span>
                      <span className="text-xs font-semibold text-gray-900 dark:text-white mt-0.5 block">
                        {ROTULOS_BENEFICIO[prontuario.recebe_beneficio] || prontuario.recebe_beneficio || 'Nenhum'}{' '}
                        {prontuario.nis ? `(NIS: ${prontuario.nis})` : ''}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3 pt-2">
                    <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800">
                      <span className="text-[10px] uppercase font-bold text-gray-400 block mb-1">
                        Órgãos de Acompanhamento (Rede Externa)
                      </span>
                      <p className="text-xs text-gray-800 dark:text-gray-200">
                        {prontuario.orgaos_acompanhamento || 'Nenhum órgão registrado.'}
                      </p>
                    </div>

                    <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800">
                      <span className="text-[10px] uppercase font-bold text-gray-400 block mb-1">
                        Observações do Serviço Social
                      </span>
                      <p className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                        {prontuario.observacoes_social || 'Nenhuma observação informada.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Sub-aba: Histórico de Ações & Atendimentos */}
              {abaProntuario === 'atendimentos' && (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                      Histórico Geral de Intervenções
                    </span>
                    {podeCriar && (
                      <Button
                        variant="primary"
                        size="sm"
                        icon={Plus}
                        className="!bg-rose-600 hover:!bg-rose-700"
                        onClick={() => abrirModalNovaAcao(prontuario)}
                      >
                        Nova Ação para este Aluno
                      </Button>
                    )}
                  </div>

                  {!prontuario.atendimentos?.length ? (
                    <div className="p-8 text-center text-gray-400 bg-gray-50/50 dark:bg-gray-800/20 rounded-2xl border border-dashed border-gray-200 dark:border-gray-800">
                      <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-xs">Nenhuma ação ou atendimento registrado para este aluno.</p>
                    </div>
                  ) : (
                    <div className="space-y-2.5 max-h-96 overflow-y-auto pr-1">
                      {prontuario.atendimentos.map((atd) => {
                        const ehAcaoGeral = atd.origem === 'acao_social';
                        return (
                          <div
                            key={`${atd.origem || 'legado'}-${atd.id}`}
                            className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800 space-y-1.5"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900 dark:text-white text-xs">
                                  {formatarDataBR(atd.data_atendimento)}
                                </span>
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400 border border-rose-200 dark:border-rose-800">
                                  {atd.tipo}
                                </span>
                                {atd.assunto && (
                                  <span className="px-1.5 py-0.2 rounded text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                    {atd.assunto}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] text-gray-400">
                                  {atd.registrado_por_nome || 'Sistema'}
                                </span>
                                {podeExcluir && !ehAcaoGeral && (
                                  <button
                                    type="button"
                                    onClick={() => mutationExcluirAtendimento.mutate(atd.id)}
                                    className="text-red-500 hover:text-red-700 p-1 rounded"
                                    title="Remover atendimento legado"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>

                            <p className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                              {atd.descricao}
                            </p>

                            {atd.nome_familiar && (
                              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                <span className="font-semibold">Familiar:</span> {atd.nome_familiar}
                              </p>
                            )}

                            {atd.encaminhamento && (
                              <p className="text-[11px] text-purple-700 dark:text-purple-300">
                                <span className="font-semibold">Encaminhamento/Externo:</span> {atd.encaminhamento}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Sub-aba: Alertas, Faltas & Ocorrências */}
              {abaProntuario === 'alertas' && (
                <div className="space-y-4">
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                      Faltas nos Últimos 60 Dias ({prontuario.faltas_recentes?.length || 0})
                    </h4>
                    {!prontuario.faltas_recentes?.length ? (
                      <p className="text-xs text-emerald-600 dark:text-emerald-400">
                        Nenhuma falta registrada no período. Assiduidade exemplar!
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {prontuario.faltas_recentes.map((f, i) => (
                          <div
                            key={i}
                            className="p-2 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/40 text-xs text-red-700 dark:text-red-400"
                          >
                            <span className="font-bold block">{formatarDataBR(f.data)}</span>
                            <span className="text-[10px] text-red-500 block truncate">
                              {f.periodo || 'Turno Regular'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                      Ocorrências Disciplinares Recentes ({prontuario.ocorrencias?.length || 0})
                    </h4>
                    {!prontuario.ocorrencias?.length ? (
                      <p className="text-xs text-gray-400">Nenhuma ocorrência disciplinar recente.</p>
                    ) : (
                      <div className="space-y-2">
                        {prontuario.ocorrencias.map((oc) => (
                          <div
                            key={oc.id}
                            className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800 text-xs"
                          >
                            <div className="flex justify-between items-center mb-1">
                              <span className="font-bold text-gray-700 dark:text-gray-300">
                                Gravidade: {oc.gravidade}
                              </span>
                              <span className="text-[10px] text-gray-400">
                                {formatarDataBR(oc.data_ocorrencia)}
                              </span>
                            </div>
                            <p className="text-gray-600 dark:text-gray-400">{oc.descricao}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>
      )}

      {/* ==================================================================== */}
      {/* MODAL: EDITAR PERFIL SOCIOECONÔMICO (PRESERVADO)                     */}
      {/* ==================================================================== */}
      <Modal
        isOpen={modalEditarPerfilAberto}
        onClose={() => setModalEditarPerfilAberto(false)}
        title="Editar Perfil Socioeconômico do Aluno"
        maxWidth="lg"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutationSalvarPerfil.mutate(perfilEditavel);
          }}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              type="number"
              step="0.01"
              label="Renda Familiar Total (R$)"
              value={perfilEditavel.renda_familiar || ''}
              onChange={(e) => setPerfilEditavel({ ...perfilEditavel, renda_familiar: e.target.value })}
              placeholder="Ex: 1500.00"
            />

            <Input
              type="number"
              label="Número de Moradores na Residência"
              value={perfilEditavel.moradores_casa || ''}
              onChange={(e) => setPerfilEditavel({ ...perfilEditavel, moradores_casa: e.target.value })}
              placeholder="Ex: 4"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select
              label="Tipo de Moradia"
              value={perfilEditavel.moradia_tipo || ''}
              onChange={(e) => setPerfilEditavel({ ...perfilEditavel, moradia_tipo: e.target.value })}
              options={[
                { value: '', label: 'Selecione...' },
                ...Object.entries(ROTULOS_MORADIA).map(([v, l]) => ({ value: v, label: l })),
              ]}
            />

            <Select
              label="Trabalho dos Responsáveis"
              value={perfilEditavel.trabalho_responsaveis || ''}
              onChange={(e) =>
                setPerfilEditavel({ ...perfilEditavel, trabalho_responsaveis: e.target.value })
              }
              options={[
                { value: '', label: 'Selecione...' },
                ...Object.entries(ROTULOS_TRABALHO).map(([v, l]) => ({ value: v, label: l })),
              ]}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Select
              label="Benefício Social"
              value={perfilEditavel.recebe_beneficio || ''}
              onChange={(e) => setPerfilEditavel({ ...perfilEditavel, recebe_beneficio: e.target.value })}
              options={[
                { value: '', label: 'Selecione...' },
                ...Object.entries(ROTULOS_BENEFICIO).map(([v, l]) => ({ value: v, label: l })),
              ]}
            />

            <Input
              type="text"
              label="Número do NIS (se houver)"
              value={perfilEditavel.nis || ''}
              onChange={(e) => setPerfilEditavel({ ...perfilEditavel, nis: e.target.value })}
              placeholder="Número do NIS"
            />

            <Select
              label="Grau de Vulnerabilidade"
              value={perfilEditavel.vulnerabilidade || 'nao_informado'}
              onChange={(e) => setPerfilEditavel({ ...perfilEditavel, vulnerabilidade: e.target.value })}
              options={Object.entries(CORES_VULNERABILIDADE).map(([v, c]) => ({
                value: v,
                label: c.label,
              }))}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Órgãos de Acompanhamento (CRAS, CREAS, Conselho Tutelar, etc.)
            </label>
            <input
              type="text"
              value={perfilEditavel.orgaos_acompanhamento || ''}
              onChange={(e) =>
                setPerfilEditavel({ ...perfilEditavel, orgaos_acompanhamento: e.target.value })
              }
              placeholder="Ex: Acompanhado pelo CRAS Ferraria e Conselho Tutelar"
              className="w-full px-3 py-2 text-xs rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Observações Gerais do Serviço Social
            </label>
            <textarea
              rows={3}
              value={perfilEditavel.observacoes_social || ''}
              onChange={(e) =>
                setPerfilEditavel({ ...perfilEditavel, observacoes_social: e.target.value })
              }
              placeholder="Anotações técnicas sobre o contexto da família..."
              className="w-full px-3 py-2 text-xs rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
            <Button type="button" variant="secondary" onClick={() => setModalEditarPerfilAberto(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={mutationSalvarPerfil.isPending}
              icon={Save}
            >
              {mutationSalvarPerfil.isPending ? 'Salvando...' : 'Salvar Perfil'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ==================================================================== */}
      {/* MODAL: INCLUIR ALUNO NO ACOMPANHAMENTO SOCIAL (PRESERVADO)           */}
      {/* ==================================================================== */}
      <Modal
        isOpen={modalIncluirAberto}
        onClose={() => {
          setModalIncluirAberto(false);
          setCandidatoSelecionado(null);
        }}
        title="Incluir Aluno no Acompanhamento Social"
        maxWidth="lg"
      >
        <div className="space-y-4">
          {!candidatoSelecionado ? (
            <div className="space-y-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Selecione um aluno da instituição que ainda não está sob acompanhamento social ativo:
              </p>
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Buscar pelo nome do aluno..."
                  value={buscaCandidato}
                  onChange={(e) => setBuscaCandidato(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800 border border-gray-100 dark:border-gray-800 rounded-xl">
                {carregandoCandidatos ? (
                  <div className="p-6 text-center text-gray-400 text-xs">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-1 text-rose-500" />
                    Buscando alunos...
                  </div>
                ) : !candidatos.length ? (
                  <div className="p-6 text-center text-gray-400 text-xs">
                    Nenhum aluno encontrado para inclusão.
                  </div>
                ) : (
                  candidatos.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => setCandidatoSelecionado(c)}
                      className="p-2.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-2.5">
                        <AvatarAluno fotoUrl={c.foto_url} nome={c.nome} tamanho="sm" />
                        <div>
                          <span className="text-xs font-semibold text-gray-900 dark:text-white block">
                            {c.nome}
                          </span>
                          <span className="text-[11px] text-gray-400">
                            Resp: {c.responsavel_nome || '—'} · {formatarIdade(c.idade, c.data_nascimento)}
                          </span>
                        </div>
                      </div>
                      <Button variant="secondary" size="sm">
                        Selecionar
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                mutationIncluirAluno.mutate();
              }}
              className="space-y-4"
            >
              <div className="p-3 rounded-xl bg-rose-50/50 dark:bg-rose-950/20 border border-rose-100 dark:border-rose-900/30 flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold text-gray-900 dark:text-white block">
                    {candidatoSelecionado.nome}
                  </span>
                  <span className="text-[11px] text-gray-500">
                    Responsável: {candidatoSelecionado.responsavel_nome || 'Não informado'}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setCandidatoSelecionado(null)}
                >
                  Trocar Aluno
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  type="number"
                  step="0.01"
                  label="Renda Familiar (R$)"
                  value={dadosInclusao.renda_familiar}
                  onChange={(e) =>
                    setDadosInclusao({ ...dadosInclusao, renda_familiar: e.target.value })
                  }
                  placeholder="Ex: 1400.00"
                />

                <Input
                  type="number"
                  label="Moradores na Casa"
                  value={dadosInclusao.moradores_casa}
                  onChange={(e) =>
                    setDadosInclusao({ ...dadosInclusao, moradores_casa: e.target.value })
                  }
                  placeholder="Ex: 4"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Select
                  label="Tipo de Moradia"
                  value={dadosInclusao.moradia_tipo}
                  onChange={(e) =>
                    setDadosInclusao({ ...dadosInclusao, moradia_tipo: e.target.value })
                  }
                  options={[
                    { value: '', label: 'Selecione...' },
                    ...Object.entries(ROTULOS_MORADIA).map(([v, l]) => ({ value: v, label: l })),
                  ]}
                />

                <Select
                  label="Trabalho dos Responsáveis"
                  value={dadosInclusao.trabalho_responsaveis}
                  onChange={(e) =>
                    setDadosInclusao({ ...dadosInclusao, trabalho_responsaveis: e.target.value })
                  }
                  options={[
                    { value: '', label: 'Selecione...' },
                    ...Object.entries(ROTULOS_TRABALHO).map(([v, l]) => ({ value: v, label: l })),
                  ]}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Select
                  label="Benefício Social"
                  value={dadosInclusao.recebe_beneficio}
                  onChange={(e) =>
                    setDadosInclusao({ ...dadosInclusao, recebe_beneficio: e.target.value })
                  }
                  options={[
                    { value: '', label: 'Selecione...' },
                    ...Object.entries(ROTULOS_BENEFICIO).map(([v, l]) => ({ value: v, label: l })),
                  ]}
                />

                <Input
                  type="text"
                  label="Número do NIS"
                  value={dadosInclusao.nis}
                  onChange={(e) => setDadosInclusao({ ...dadosInclusao, nis: e.target.value })}
                  placeholder="Opcional"
                />

                <Select
                  label="Grau de Vulnerabilidade"
                  value={dadosInclusao.vulnerabilidade}
                  onChange={(e) =>
                    setDadosInclusao({ ...dadosInclusao, vulnerabilidade: e.target.value })
                  }
                  options={Object.entries(CORES_VULNERABILIDADE).map(([v, c]) => ({
                    value: v,
                    label: c.label,
                  }))}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Órgãos de Acompanhamento (CRAS, CREAS, Conselho Tutelar, etc.)
                </label>
                <input
                  type="text"
                  value={dadosInclusao.orgaos_acompanhamento}
                  onChange={(e) =>
                    setDadosInclusao({ ...dadosInclusao, orgaos_acompanhamento: e.target.value })
                  }
                  placeholder="Ex: Acompanhado pelo CRAS Ferraria"
                  className="w-full px-3 py-2 text-xs rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Observações Gerais do Diagnóstico Social
                </label>
                <textarea
                  rows={2}
                  value={dadosInclusao.observacoes_social}
                  onChange={(e) =>
                    setDadosInclusao({ ...dadosInclusao, observacoes_social: e.target.value })
                  }
                  placeholder="Anotações iniciais..."
                  className="w-full px-3 py-2 text-xs rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setModalIncluirAberto(false);
                    setCandidatoSelecionado(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={mutationIncluirAluno.isPending}
                  icon={Save}
                >
                  {mutationIncluirAluno.isPending ? 'Salvando...' : 'Confirmar Inclusão'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </Modal>

      {/* ==================================================================== */}
      {/* MODAL: NOVO ATENDIMENTO INDIVIDUAL LEGADO (PRESERVADO)               */}
      {/* ==================================================================== */}
      <Modal
        isOpen={modalAtendimentoAberto}
        onClose={() => setModalAtendimentoAberto(false)}
        title="Registrar Atendimento Social Individual"
        maxWidth="md"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutationAtendimento.mutate(novoAtendimento);
          }}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select
              label="Tipo de Atendimento"
              value={novoAtendimento.tipo}
              onChange={(e) => setNovoAtendimento({ ...novoAtendimento, tipo: e.target.value })}
              options={TIPOS_ATENDIMENTO_LEGADO}
            />

            <Input
              type="date"
              label="Data do Atendimento"
              value={novoAtendimento.data_atendimento}
              onChange={(e) =>
                setNovoAtendimento({ ...novoAtendimento, data_atendimento: e.target.value })
              }
              required
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Descrição do Atendimento / Demanda *
            </label>
            <textarea
              rows={4}
              value={novoAtendimento.descricao}
              onChange={(e) => setNovoAtendimento({ ...novoAtendimento, descricao: e.target.value })}
              placeholder="Descreva o motivo do atendimento, escuta e situação apresentada..."
              className="w-full px-3 py-2 text-xs rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>

          <Input
            label="Encaminhamento Realizado"
            value={novoAtendimento.encaminhamento}
            onChange={(e) =>
              setNovoAtendimento({ ...novoAtendimento, encaminhamento: e.target.value })
            }
            placeholder="Ex: Encaminhado ao CRAS / Posto de Saúde"
          />

          <Input
            label="Benefício Concedido (se houver)"
            value={novoAtendimento.beneficio_concedido}
            onChange={(e) =>
              setNovoAtendimento({ ...novoAtendimento, beneficio_concedido: e.target.value })
            }
            placeholder="Ex: Cesta básica, isenção de mensalidade..."
          />

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setModalAtendimentoAberto(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={mutationAtendimento.isPending}
              icon={Save}
            >
              {mutationAtendimento.isPending ? 'Salvando...' : 'Salvar Atendimento'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default AssistenciaSocial;
